import { type RecordOf as ImRecordOf, Set as ImmutableSet, Record } from 'immutable';
import type { RecordOf } from 'immutable';

import type { QcsSyncResourceStore } from 'app-components/flux/core/resourceStoreTypes';
import type { DateTime } from 'luxon';
import { resourceStoreTypes } from '../core';
import { Customer } from '../customers/customersTypes';
import { Project } from '../projects/projectsTypes';

// ================================================================================================
// FilterStore - Filter Store Properties & Types
// ================================================================================================

export const FILTERS_REDUCER_KEY = 'filterStore';

export type FiltersStore = QcsSyncResourceStore<FilterProps>;

export const mkFiltersStore = resourceStoreTypes.createTypedQcsSyncResourceStoreFactory<FilterProps>();

// ================================================================================================
// FilterItems - FilterItems Store Properties & Types
// ================================================================================================
export type FilterItemProps =
  | {
      type: 'TAG';
      tags: {
        id: string;
      }[];
    }
  | {
      type: 'PAYEE';
      payees: string[];
    }
  | {
      type: 'CHARTOFACCOUNT';
      chartOfAccounts: { id: string; type: string }[];
    }
  | {
      type: 'ACCOUNT';
      accounts: { id: string }[];
    }
  | {
      type: 'USAGE';
      usages: { id: string; type: string }[];
    }
  | {
      type: 'CUSTOMERPROJECT';
      customerProjects: { customer: string; projects: string[] }[];
    };

export type FilterItems = RecordOf<FilterItemProps>;
export const makeFilterItem = Record({
  type: null,
  id: null,
});

// ================================================================================================
// Filter - Filter Properties & Types
// ================================================================================================

export type FilterProps = {
  isDeleted?: boolean;
  type?: string;
  id?: string;
  parentIds?: string[];
  filterItems?: FilterItemProps[];
  clientId?: string;
};

export type Filter = ImRecordOf<FilterProps>;

export const mkFilter = Record<FilterProps>({
  id: undefined,
  isDeleted: undefined,
  type: undefined,
  parentIds: undefined,
  filterItems: undefined,
  clientId: undefined,
});

export const AdvancedFilterObject = Record<AdvancedFilter>({
  isBillOrSubscription: undefined,
  isExcludedFromReports: undefined,
  isReviewed: undefined,
  isExcludedFromF2S: undefined,
});

export type AdvancedFilter = {
  isBillOrSubscription?: boolean;
  isExcludedFromReports?: boolean;
  isReviewed?: boolean;
  isExcludedFromF2S?: boolean;
};

export type CustomerAndProjects = {
  customer: Customer;
  projects: Project[];
};

export type FilterObjectProps = {
  categories?: ImmutableSet<string>;
  customersAndProjectsSelected?: ImmutableSet<CustomerAndProjects>;
  tags?: ImmutableSet<string>;
  accounts?: ImmutableSet<string>;
  payeeNames?: ImmutableSet<string>;
  flags?: ImmutableSet<string>;
  businesses?: ImmutableSet<string>;
  advanced?: ImRecordOf<AdvancedFilter>;
  numFilterItems?: number;
  filterOut?: boolean;
  dateStart?: DateTime | string | null;
  dateEnd?: DateTime | string | null;
};
export type FilterObject = ImRecordOf<FilterObjectProps>;
export const mkFilterObject = Record<FilterObjectProps>({
  categories: ImmutableSet(),
  customersAndProjectsSelected: ImmutableSet(),
  tags: ImmutableSet(),
  accounts: ImmutableSet(),
  payeeNames: ImmutableSet(),
  flags: ImmutableSet(),
  businesses: ImmutableSet(),
  advanced: AdvancedFilterObject(),
  numFilterItems: 0,
  filterOut: false, // filter items IN or OUT
  dateStart: null,
  dateEnd: null,
});

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [FILTERS_REDUCER_KEY]: QcsSyncResourceStore<FilterProps>;
  }
}
